import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { friendlyError } from "@/lib/friendlyError";

export interface ApprovalRequest {
  id: string;
  record_type: string;
  record_id: string | null;
  record_name: string;
  change_type: string;
  change_summary: string;
  change_diff: Record<string, { old: any; new: any }>;
  submitted_by: string;
  submitted_by_name: string;
  submitted_by_role: string;
  status: string;
  reviewed_by: string | null;
  reviewed_by_name: string;
  reviewed_at: string | null;
  rejection_reason: string;
  info_request_message: string;
  info_response: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export function useApprovals(statusFilter?: string) {
  return useQuery({
    queryKey: ["approvals", statusFilter],
    queryFn: async () => {
      let query = supabase
        .from("approval_requests")
        .select("*")
        .order("created_at", { ascending: false });

      if (statusFilter && statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as unknown as ApprovalRequest[];
    },
  });
}

export function usePendingApprovalCount() {
  return useQuery({
    queryKey: ["approvals", "pending_count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("approval_requests")
        .select("*", { count: "exact", head: true })
        .eq("status", "pending");
      if (error) throw error;
      return count ?? 0;
    },
    refetchInterval: 30_000,
  });
}

export function useApproveRequest() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ id, reviewerName }: { id: string; reviewerName: string }) => {
      const { error } = await supabase
        .from("approval_requests")
        .update({
          status: "approved",
          reviewed_by_name: reviewerName,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approvals"] });
      toast({ title: "Change approved ✅" });
    },
    onError: (e: Error) => {
      toast({ title: "Could not approve that", description: friendlyError(e), variant: "destructive" });
    },
  });
}

export function useRejectRequest() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ id, reviewerName, reason }: { id: string; reviewerName: string; reason: string }) => {
      const { error } = await supabase
        .from("approval_requests")
        .update({
          status: "rejected",
          reviewed_by_name: reviewerName,
          reviewed_at: new Date().toISOString(),
          rejection_reason: reason,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approvals"] });
      toast({ title: "Change rejected" });
    },
    onError: (e: Error) => {
      toast({ title: "Could not decline that", description: friendlyError(e), variant: "destructive" });
    },
  });
}

export function useRequestMoreInfo() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ id, reviewerName, message }: { id: string; reviewerName: string; message: string }) => {
      const { error } = await supabase
        .from("approval_requests")
        .update({
          status: "info_requested",
          reviewed_by_name: reviewerName,
          info_request_message: message,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approvals"] });
      toast({ title: "Information requested" });
    },
    onError: (e: Error) => {
      toast({ title: "Could not save that", description: friendlyError(e), variant: "destructive" });
    },
  });
}
